import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { DebounceInput } from 'react-debounce-input'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import XLSX from 'xlsx'

import { httpOk, GET } from 'helpers/api'
import { formatDate } from 'helpers/helpers'

const STATE_CLASSES = {
	sent: 'text-success',
	orphan: 'text-warning',
	blocked: 'text-danger',
}

function Index() {
	const { t } = useTranslation()
	const { id } = useParams()

	const [data, setData] = useState(null)
	const [posId, setPosId] = useState(null)
	const [search, setSearch] = useState('')
	const [stateFilter, setStateFilter] = useState('all')
	const [reasonFilter, setReasonFilter] = useState(null)
	const [showJson, setShowJson] = useState(false)
	const [jsonTab, setJsonTab] = useState('restaurants')
	const [payload, setPayload] = useState(null)
	const [copied, setCopied] = useState(null)

	async function searchWithFilter(nextPosId = posId) {
		var filterUrl = `/external-integrations/${id}/menu-diagnostics`
		if (nextPosId) filterUrl += `?pos_id=${nextPosId}`

		var response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPosId(response.data.pos_id ?? null)
		}
	}

	// то, что реально уходит агрегатору — тянем только когда открыли вкладку JSON
	async function getPayload(nextPosId = posId) {
		var payloadUrl = `/external-integrations/${id}/vendor-payload`
		if (nextPosId) payloadUrl += `?pos_id=${nextPosId}`

		var response = await GET(payloadUrl, {}, { loader: true })
		if (httpOk(response)) setPayload(response.data)
	}

	function toggleJsonModal(bool = false) {
		setShowJson(bool)
		if (bool) {
			setJsonTab('restaurants')
			getPayload()
		}
	}

	function handleSelectPos(option) {
		const nextPosId = option ? option.id : null
		setPosId(nextPosId)
		setPayload(null)
		searchWithFilter(nextPosId)
		if (showJson) getPayload(nextPosId)
	}

	function copyJson(name, value) {
		navigator.clipboard.writeText(JSON.stringify(value, null, 2))
		setCopied(name)
		setTimeout(() => setCopied(null), 2000)
	}

	function handleStateFilter(state) {
		setStateFilter(state)
		setReasonFilter(null)
	}

	function handleReasonFilter(code) {
		setReasonFilter(reasonFilter === code ? null : code)
		setStateFilter('all')
	}

	function stateText(state) {
		return t(`external_menu_check_state_${state}`)
	}

	function reasonText(reason) {
		return t(`external_menu_check_reason_${reason.code}`)
	}

	// позиция уходит агрегатору, но изменена после его последней выгрузки меню по этой точке
	function isNotPulled(item) {
		return item.will_be_sent && item.is_not_pulled
	}

	function notPulledText(item) {
		return `${t('menu_not_pulled_item')} (${formatDate(item.changed_at, 'dd.MM.yyyy HH:mm')})`
	}

	function checkTitle(check) {
		return t(`external_menu_check_check_${check.code}`)
	}

	function checkDescription(check) {
		if (check.status !== 'ok') return t(`external_menu_check_error_${check.code}`)

		return check.description || t(`external_menu_check_ok_${check.code}`)
	}

	const items = useMemo(() => {
		if (!data?.items) return []

		const query = search.trim().toLowerCase()

		return data.items.filter((item) => {
			if (stateFilter === 'not_pulled') {
				if (!isNotPulled(item)) return false
			} else if (stateFilter !== 'all' && item.state !== stateFilter) return false

			if (reasonFilter) {
				const codes = [...item.errors, ...item.warnings].map((reason) => reason.code)
				if (!codes.includes(reasonFilter)) return false
			}

			if (query) {
				const haystack = [item.name, item.system_name, item.category_name, item.product_id]
					.filter(Boolean)
					.join(' ')
					.toLowerCase()
				if (!haystack.includes(query)) return false
			}

			return true
		})
	}, [data, search, stateFilter, reasonFilter])

	function downloadExcelFile() {
		const excelHeaders = [[
			'ID', t('product'), t('system_name2'), t('category'),
			t('price'), t('weight'), t('unit'), t('status'), t('external_menu_check_reasons'),
		]]

		for (let i = 0; i < items.length; i++) {
			const item = items[i]
			excelHeaders.push([
				item.product_id,
				item.name,
				item.system_name,
				item.category_name,
				item.price,
				item.weight,
				item.unit,
				stateText(item.state),
				[...item.errors, ...item.warnings].map((reason) => reasonText(reason))
					.concat(isNotPulled(item) ? [notPulledText(item)] : [])
					.join(', '),
			])
		}

		const ws = XLSX.utils.aoa_to_sheet(excelHeaders);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
		XLSX.writeFile(wb, t('external_menu_check') + ".xlsx");
	}

	function productLink(item) {
		if (!data?.integration?.external_group_id) return null

		var link = `/external-menu/${data.integration.external_group_id}?product=${item.id}`
		if (item.category_id) link += `&menu=${item.category_id}`

		return link
	}

	useEffect(() => {
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const summary = data?.summary

	const summaryCards = summary ? [
		{
			state: 'all',
			label: t('external_menu_check_total'),
			value: summary.count_products,
			className: '',
			hint: `${t('external_menu_check_total_hint')}: ${summary.count_categories_sent} / ${summary.count_categories}`,
		},
		{
			state: 'sent',
			label: t('external_menu_check_sent'),
			value: summary.count_sent,
			className: 'text-success',
			hint: t('external_menu_check_sent_hint'),
		},
		{
			state: 'blocked',
			label: t('external_menu_check_blocked'),
			value: summary.count_blocked,
			className: 'text-danger',
			hint: t('external_menu_check_blocked_hint'),
		},
		{
			state: 'orphan',
			label: t('external_menu_check_orphan'),
			value: summary.count_orphan,
			className: 'text-warning',
			hint: t('external_menu_check_orphan_hint'),
		},
	] : []

	const vendor = payload?.integration?.provider_value
	const payloadBlocks = []
	const payloadWarnings = []

	if (payload) {
		payloadBlocks.push({
			code: 'restaurants',
			title: 'external_menu_check_payload_restaurants',
			url: `GET /integrations/${vendor}/restaurants`,
			count: (payload.restaurants?.places ?? []).length,
			value: payload.restaurants,
		})

		if (payload.composition) {
			payloadBlocks.push({
				code: 'composition',
				title: 'external_menu_check_payload_composition',
				url: `GET /integrations/${vendor}/menu/${payload.pos_id}/composition`,
				count: payload.composition.items.length,
				value: payload.composition,
			})
		}

		payloadBlocks.push({
			code: 'diagnostics',
			title: 'external_menu_check_payload_diagnostics',
			url: null,
			count: items.length,
			value: items,
		})

		if (payload.composition_error) {
			payloadWarnings.push(`external_menu_check_payload_error_${payload.composition_error}`)
		}

		if ((payload.restaurants?.places ?? []).some(place => !place.address)) {
			payloadWarnings.push('external_menu_check_payload_empty_address')
		}

		if (payload.composition && !payload.composition.categories.length && !payload.composition.items.length) {
			payloadWarnings.push('external_menu_check_payload_empty_menu')
		}
	}

	const activeBlock = payloadBlocks.find(block => block.code === jsonTab) ?? payloadBlocks[0]

	return (
		<>
			<div className="page-header d-flex justify-content-between gap-2">
				<h4 className="vertical-center text-truncate mb-0">
					{t('external_menu_check')}
					{data?.integration?.name &&
						<span className="text-muted fz-14 ms-2">{data.integration.name}</span>
					}
				</h4>
				<div className="d-flex gap-2 flex-shrink-0">
					<Link className="btn btn-outline-primary" to={`/external-integrations/update/${id}`}>
						{t('external_menu_check_to_integration')}
					</Link>
					{data?.integration?.external_group_id &&
						<Link className="btn btn-outline-primary" to={`/external-menu/${data.integration.external_group_id}`}>
							{t('external_menu_check_to_menu')}
						</Link>
					}
					<button className="btn btn-primary"
						disabled={!items.length}
						onClick={() => downloadExcelFile()}>
						<i className="uil uil-file-download-alt me-1"></i>
						{t('download')}
					</button>
				</div>
			</div>

			{data &&
				<div className="card mb-2">
					<div className="card-body p-2">
						<div className="row">
							<div className="col-md-4">
								<div className="form-group mb-0">
									<label className="fz-12 mb-1">{t('pos')}</label>
									<Select
										isClearable
										options={data.poses ?? []}
										value={(data.poses ?? []).find(option => option.id === posId) || null}
										onChange={(option) => handleSelectPos(option)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.status
											? option.name
											: `${option.name} (${t('external_menu_check_pos_disabled')})`}
										getOptionValue={(option) => option.id}
									/>
									<div className="text-muted fz-12 mt-1">
										{t('external_menu_check_pos_hint')}
									</div>
								</div>
							</div>
							<div className="col-md-8">
								<label className="fz-12 mb-1">{t('external_menu_check_settings')}</label>
								{data.checks?.map((check, index) => (
									<div key={index} className="d-flex gap-2 fz-12">
										{check.status === 'ok' ?
											<i className="uil uil-check-circle text-success"></i>
											:
											<i className="uil uil-exclamation-triangle text-danger"></i>
										}
										<span className="fw-500">{checkTitle(check)}:</span>
										<span className={check.status === 'ok' ? 'text-muted' : 'text-danger'}>
											{checkDescription(check)}
										</span>
									</div>
								))}
								{data.pull && posId &&
									<div className="d-flex gap-2 fz-12">
										{data.pull.is_menu_outdated ?
											<i className="uil uil-exclamation-triangle text-warning"></i>
											:
											<i className="uil uil-check-circle text-success"></i>
										}
										<span className="fw-500">{t('menu_pulled_at')}:</span>
										<span className={data.pull.is_menu_outdated ? 'text-warning' : 'text-muted'}>
											{data.pull.menu_pulled_at
												? formatDate(data.pull.menu_pulled_at, 'dd.MM.yyyy HH:mm')
												: t('menu_never_pulled')
											}
											{data.pull.menu_changed_at &&
												<> · {t('menu_changed_at')}: {formatDate(data.pull.menu_changed_at, 'dd.MM.yyyy HH:mm')}</>
											}
											{data.pull.is_menu_outdated &&
												<> · {t('menu_ask_to_reload')}</>
											}
										</span>
									</div>
								}
							</div>
						</div>
					</div>
				</div>
			}

			{summary &&
				<div className="card mb-2">
					<div className="card-body p-2">
						<div className="row g-2">
							{summaryCards.map((card, index) => (
								<div key={index} className="col-md-3">
									<div className={`border rounded p-2 cursor ${stateFilter === card.state ? 'border-primary' : ''}`}
										onClick={() => handleStateFilter(card.state)}>
										<div className="d-flex justify-content-between">
											<span className="text-muted fz-12">{card.label}</span>
											<span className={`fw-600 fz-18 ${card.className}`}>{card.value}</span>
										</div>
										<div className="text-muted fz-12 text-truncate" title={card.hint}>
											{card.hint}
										</div>
									</div>
								</div>
							))}
						</div>

						{(summary.reasons?.length > 0 || summary.count_not_pulled > 0) &&
							<div className="d-flex flex-wrap gap-1 mt-2">
								{summary.count_not_pulled > 0 &&
									<button type="button"
										className={`btn btn-sm fz-12 ${stateFilter === 'not_pulled' ? 'btn-warning' : 'btn-outline-warning'}`}
										onClick={() => handleStateFilter(stateFilter === 'not_pulled' ? 'all' : 'not_pulled')}>
										{t('menu_not_pulled_item')}
										<span className="ms-1 fw-600">{summary.count_not_pulled}</span>
									</button>
								}
								{summary.reasons.map((reason, index) => (
									<button key={index} type="button"
										className={`btn btn-sm fz-12 ${reasonFilter === reason.code ? 'btn-primary' : 'btn-outline-primary'}`}
										onClick={() => handleReasonFilter(reason.code)}>
										{reasonText(reason)}
										<span className="ms-1 fw-600">{reason.count}</span>
									</button>
								))}
							</div>
						}
					</div>
				</div>
			}

			<div className="card">
				<div className="card-body p-2">
					<div className="d-flex justify-content-between mb-2 gap-2">
						<div className="position-relative w-50">
							<i className="uil-search input-search-icon"></i>
							<DebounceInput
								type="text"
								className="form-control"
								placeholder={t('search') + '...'}
								debounceTimeout={300}
								value={search}
								onChange={(e) => setSearch(e.target.value)} />
						</div>
						<div className="d-flex gap-2">
							<div className="vertical-center text-muted">
								{t('external_menu_check_shown')}: {items.length}
							</div>
							<button type="button"
								className="btn btn-outline-dark"
								onClick={() => toggleJsonModal(true)}>
								<i className="uil uil-brackets-curly me-1"></i>
								JSON
							</button>
						</div>
					</div>

					<div className="table-responsive">
						<table className="table table-bordered table-sm fz-12 mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '20%' }}>{t('name2')}</th>
									<th style={{ 'width': '15%' }}>{t('system_name2')}</th>
									<th style={{ 'width': '15%' }}>{t('category')}</th>
									<th style={{ 'width': '8%' }} className="text-end">{t('price')}</th>
									<th style={{ 'width': '12%' }} className="text-center">{t('status')}</th>
									<th style={{ 'width': '25%' }}>{t('external_menu_check_reasons')}</th>
									<th style={{ 'minWidth': '33px' }} className="text-center"></th>
								</tr>
							</thead>
							<tbody>
								{items.map((item, index) => (
									<tr key={item.id}
										className={item.state === 'blocked' ? 'table-danger' : (item.state === 'orphan' ? 'table-warning' : '')}>
										<td className="text-center">{index + 1}</td>
										<td>{item.name}</td>
										<td className="text-muted">{item.system_name}</td>
										<td>{item.category_name ?? <span className="text-danger">—</span>}</td>
										<td className="text-end">{item.price}</td>
										<td className={`text-center fw-500 ${STATE_CLASSES[item.state]}`}>
											{stateText(item.state)}
										</td>
										<td>
											{item.errors.map((reason, index2) => (
												<div key={`e-${index2}`} className="text-danger">{reasonText(reason)}</div>
											))}
											{item.warnings.map((reason, index2) => (
												<div key={`w-${index2}`} className="text-warning">{reasonText(reason)}</div>
											))}
											{isNotPulled(item) &&
												<div className="text-primary">{notPulledText(item)}</div>
											}
										</td>
										<td className="vertical-middle">
											{productLink(item) &&
												<div className="d-flex justify-content-center">
													<Link className="table-action-button table-action-primary-button"
														title={t('external_menu_check_fix')}
														to={productLink(item)}>
														<i className="uil uil-pen"></i>
													</Link>
												</div>
											}
										</td>
									</tr>
								))}
								{!items.length &&
									<tr>
										<td colSpan={8} className="text-center text-muted">
											{t('list_empty')}
										</td>
									</tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* MODAL */}
			<Modal show={showJson} animation={false} centered size="xl"
				dialogClassName="vendor-payload-modal" onHide={() => toggleJsonModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('external_menu_check_payload_title')}
						{payload?.integration?.name &&
							<span className="text-muted fz-14 ms-2">{payload.integration.name}</span>
						}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<style>{`
						.vendor-payload-modal .modal-dialog {
							max-width: 1140px;
						}
						.vendor-payload-modal pre {
							max-height: 60vh;
							overflow: auto;
							font-size: 12px;
						}
					`}</style>

					<div className="text-muted fz-12 mb-2">
						{t('external_menu_check_payload_hint')}
					</div>

					{payloadWarnings.map((warning, index) => (
						<div key={index} className="alert alert-danger fz-12 py-1 px-2 mb-1">
							<i className="uil uil-exclamation-triangle me-1"></i>
							{t(warning)}
						</div>
					))}

					{activeBlock &&
						<>
							<div className="d-flex justify-content-between gap-2 mt-2 mb-2">
								<div className="d-flex flex-wrap gap-1">
									{payloadBlocks.map((block, index) => (
										<button key={index} type="button"
											className={`btn btn-sm fz-12 ${activeBlock.code === block.code ? 'btn-primary' : 'btn-outline-primary'}`}
											onClick={() => setJsonTab(block.code)}>
											{t(block.title)}
											<span className="ms-1 fw-600">{block.count}</span>
										</button>
									))}
								</div>
								<button type="button"
									className="btn btn-sm btn-outline-dark fz-12 flex-shrink-0"
									onClick={() => copyJson(activeBlock.code, activeBlock.value)}>
									<i className="uil uil-copy me-1"></i>
									{copied === activeBlock.code ? t('external_menu_check_copied') : t('external_menu_check_copy')}
								</button>
							</div>

							{activeBlock.url &&
								<div className="text-muted fz-12 mb-1">{activeBlock.url}</div>
							}

							<pre className="bg-light border rounded p-3 mb-0">
								{JSON.stringify(activeBlock.value, null, 2)}
							</pre>
						</>
					}
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Index
